import React, { useState } from 'react';
import { Phone, Mail, ArrowRight } from "lucide-react";
import emailjs from '@emailjs/browser';

const labelClass = "block text-[#60a5fa] font-semibold uppercase mb-2.5" +
  " text-[10px] tracking-[0.18em]";

function Field({ label, children }) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      {children}
    </div>
  );
}

const baseInput = {
  backgroundColor: '#1a2a3a',
  borderBottom: '1px solid rgba(37,99,235,0.3)',
  borderTop: 'none',
  borderRight: 'none',
  color: '#fff',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  padding: '14px 16px',
  fontSize: '16px',
  outline: 'none',
  transition: 'border-left 0.15s, box-shadow 0.15s',
  borderLeft: '3px solid transparent',
  fontStyle: 'normal',
  minWidth: 0,
};

function StyledInput({ type = "text", name, placeholder, value, onChange, required }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      required={required}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        ...baseInput,
        borderLeft: focused ? '3px solid #2563eb' : '3px solid transparent',
        boxShadow: focused ? '-3px 0 12px rgba(37,99,235,0.18)' : 'none',
      }}
    />
  );
}

function StyledTextarea({ name, placeholder, value, onChange, rows, required }) {
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      rows={rows}
      required={required}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        ...baseInput,
        resize: 'none',
        minHeight: '140px',
        borderLeft: focused ? '3px solid #2563eb' : '3px solid transparent',
        boxShadow: focused ? '-3px 0 12px rgba(37,99,235,0.18)' : 'none',
        overflowX: 'hidden',
      }}
    />
  );
}

const serviceTypes = {
  Commercial: [
    'Commercial Shopfit Plumbing',
    'Retail & Hospitality Fitout',
    'New Commercial Build',
    'Commercial Kitchen Plumbing',
    'Grease Trap Installation',
    'Maintenance & Repairs',
    'Builder & Project Coordination',
    'Backflow Prevention',
    'Other (please describe below)',
  ],
};

function StyledSelect({ name, value, onChange, options, placeholder }) {
  const [focused, setFocused] = useState(false);
  return (
    <select
      aria-label={placeholder || name}
      name={name}
      value={value}
      onChange={onChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        ...baseInput,
        appearance: 'none',
        cursor: 'pointer',
        borderLeft: focused ? '3px solid #2563eb' : '3px solid transparent',
        boxShadow: focused ? '-3px 0 12px rgba(37,99,235,0.18)' : 'none',
      }}
    >
      <option value="" disabled style={{ background: '#1a2a3a' }}>{placeholder || 'Select…'}</option>
      {options.map(opt => (
        <option key={opt} value={opt} style={{ background: '#1a2a3a' }}>{opt}</option>
      ))}
    </select>
  );
}

export default function CTASection() {
  const emptyForm = { name: '', phone: '', email: '', company: '', project_location: '', serviceCategory: '', serviceType: '', message: '' };
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [privacyError, setPrivacyError] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'serviceCategory') {
      setForm(prev => ({ ...prev, serviceCategory: value, serviceType: '' }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!privacyChecked) {
      setPrivacyError(true);
      return;
    }
    setPrivacyError(false);
    setErrorMsg('');

    if (!form.email && !form.phone) {
      setErrorMsg('Please provide at least a phone number or email address.');
      return;
    }

    setSubmitting(true);

    const timeoutId = setTimeout(() => {
      setSubmitting(false);
      setErrorMsg('Request timed out. Please try again or call us directly.');
    }, 10000);

    try {
  const result = await emailjs.send(
  'service_ctby6n6',
  'template_q3xm9ln',
  {
    name: form.name,
    phone: form.phone,
    email: form.email,
    company: form.company || 'N/A',
    service_category: form.serviceCategory || 'N/A',
    service_type: form.serviceType || 'N/A',
    project_location: form.projectLocation || 'N/A',
    message: form.message || 'N/A',
  },
  'GmigOsjKebG4eXsh6'
);
      console.log('EmailJS response:', result);
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          send_to: 'AW-17442634907/xAh-CL762EdEvJv5pf1A',
          value: 1.0,
          currency: 'AUD',
        });
      }
      clearTimeout(timeoutId);
      setForm(emptyForm);
      setPrivacyChecked(false);
      setSuccessMsg(true);
      setTimeout(() => setSuccessMsg(false), 5000);
    } catch (err) {
      clearTimeout(timeoutId);
      console.error('EmailJS error:', err);
      setErrorMsg('Something went wrong. Please call us on 0403 448 370 or email info@oliverandsons.com.au');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="relative">
      {/* Top blue border */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#2563eb] z-10" />

      <div className="grid md:grid-cols-2">
        {/* LEFT — Dark navy */}
        <div className="bg-[#0d1f33] px-5 sm:px-8 md:px-14 lg:px-16 py-14 md:py-24 flex flex-col justify-center">
          <p className="text-[#60a5fa] text-xs font-semibold tracking-[0.25em] uppercase mb-5">Ready to Build?</p>
          <h2 className="font-bold text-white leading-[1.1] tracking-tight mb-5" style={{ fontSize: 'clamp(1.6rem, 5vw, 2.8rem)' }}>
            Let's Talk About<br />Your Next Project.
          </h2>
          <p className="text-gray-400 text-base leading-relaxed mb-7 max-w-sm">
            We work directly with builders, shopfitters and developers across South East Queensland. Get in touch and we'll get back to you fast.
          </p>

          {/* Contact rows */}
          <div className="flex flex-col gap-4 mb-8">
            <a href="tel:0403448370" className="flex items-center gap-3 text-white hover:text-[#5a9cf8] transition-colors duration-200 group">
              <div className="w-9 h-9 rounded-full bg-[#1e3a5f]/50 border border-[#2563eb]/30 flex items-center justify-center group-hover:border-[#2563eb]/60 transition-colors">
                <Phone className="w-4 h-4 text-[#2563eb]" />
              </div>
              <span className="text-base font-medium">0403 448 370</span>
            </a>
            <a href="mailto:info@oliverandsons.com.au" className="flex items-center gap-3 text-white hover:text-[#5a9cf8] transition-colors duration-200 group">
              <div className="w-9 h-9 rounded-full bg-[#1e3a5f]/50 border border-[#2563eb]/30 flex items-center justify-center group-hover:border-[#2563eb]/60 transition-colors">
                <Mail className="w-4 h-4 text-[#2563eb]" />
              </div>
              <span className="text-base font-medium">info@oliverandsons.com.au</span>
            </a>
          </div>

          {/* Call button */}
          <a
            href="tel:0403448370"
            className="flex items-center justify-center gap-3 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-bold text-base py-4 px-6 transition-all duration-300 hover:shadow-lg hover:shadow-[#2563eb]/30 rounded-sm"
          >
            <Phone className="w-5 h-5" />
            Call Us Now — 0403 448 370
          </a>
          <p className="text-gray-400 text-xs mt-2 text-center tracking-wide">Mon – Fri, 8am – 5pm</p>

          {/* Email button */}
          <a
            href="mailto:info@oliverandsons.com.au"
            className="mt-3 flex items-center justify-center gap-3 border border-[#2563eb]/40 hover:border-[#2563eb] text-white hover:text-white font-bold text-base py-4 px-6 transition-all duration-300 hover:bg-[#2563eb]/10 rounded-sm"
          >
            <Mail className="w-5 h-5 text-[#2563eb]" />
            Email Us Now
          </a>
        </div>

        {/* RIGHT — Form */}
        <div
          className="relative px-5 sm:px-8 md:px-14 lg:px-16 py-12 md:py-24 flex flex-col justify-center overflow-hidden"
          style={{ backgroundColor: '#0a1220', boxSizing: 'border-box', overflowX: 'hidden' }}
        >
          {/* Subtle grid texture */}
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.035]"
            style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
              backgroundSize: '40px 40px',
            }}
          />
          <div className="relative z-10" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
              <form onSubmit={handleSubmit} className="space-y-7" style={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                <Field label="Your Name">
                  <StyledInput type="text" name="name" required placeholder="e.g. James Mitchell" value={form.name} onChange={handleChange} />
                </Field>
                <Field label={<>Phone Number <span style={{ color: '#6b7280', fontWeight: 400, textTransform: 'none', letterSpacing: 'normal', fontSize: '10px' }}>— Optional</span></>}>
                  <StyledInput type="tel" name="phone" placeholder="04XX XXX XXX" value={form.phone} onChange={handleChange} />
                </Field>
                <Field label={<>Email Address <span style={{ color: '#6b7280', fontWeight: 400, textTransform: 'none', letterSpacing: 'normal', fontSize: '10px' }}>— Optional</span></>}>
                  <StyledInput type="email" name="email" placeholder="your@email.com" value={form.email} onChange={handleChange} />
                </Field>
                <Field label={<>Company Name <span style={{ color: '#6b7280', fontWeight: 400, textTransform: 'none', letterSpacing: 'normal', fontSize: '10px' }}>— Optional</span></>}>
                  <StyledInput type="text" name="company" placeholder="Company or trading name" value={form.company} onChange={handleChange} />
                </Field>
                <Field label={<>Project Location <span style={{ color: '#6b7280', fontWeight: 400, textTransform: 'none', letterSpacing: 'normal', fontSize: '10px' }}>— Optional</span></>}>
                  <StyledInput type="text" name="project_location" placeholder="e.g. Brisbane CBD" value={form.project_location} onChange={handleChange} />
                </Field>
                <Field label="Service Category">
                  <StyledSelect
                    name="serviceCategory"
                    value={form.serviceCategory}
                    onChange={handleChange}
                    options={['Commercial', 'Other']}
                    placeholder="Select a category…"
                  />
                </Field>
                {form.serviceCategory && form.serviceCategory !== 'Other' && (
                  <div style={{ animation: 'fadeIn 0.25s ease' }}>
                    <Field label="Service Type">
                      <StyledSelect
                        name="serviceType"
                        value={form.serviceType}
                        onChange={handleChange}
                        options={serviceTypes[form.serviceCategory] || []}
                        placeholder="Select a service…"
                      />
                    </Field>
                  </div>
                )}
                <Field label="Your Message">
                  <StyledTextarea
                    name="message"
                    placeholder={form.serviceCategory === 'Other' ? 'Please describe what you need...' : 'Tell us about your project...'}
                    rows={3}
                    value={form.message}
                    onChange={handleChange}
                    required={form.serviceCategory === 'Other'}
                  />
                </Field>
                {/* Privacy checkbox */}
                <div>
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <div className="relative flex-shrink-0 mt-0.5">
                      <input
                        type="checkbox"
                        checked={privacyChecked}
                        onChange={e => { setPrivacyChecked(e.target.checked); if (e.target.checked) setPrivacyError(false); }}
                        className="sr-only"
                      />
                      <div
                        className="w-4 h-4 border transition-all duration-200 flex items-center justify-center"
                        style={{
                          backgroundColor: privacyChecked ? '#2563eb' : 'transparent',
                          borderColor: privacyError ? '#ef4444' : privacyChecked ? '#2563eb' : 'rgba(37,99,235,0.4)',
                        }}
                      >
                        {privacyChecked && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth={2.5}>
                            <path d="M2 6l3 3 5-5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <span className="text-gray-400 text-xs leading-relaxed">
                      I have read and agree to the{' '}
                      <a href="/privacy-policy#privacy-policy" className="text-[#2563eb] hover:text-blue-400 underline transition-colors">Privacy Policy</a>
                      {' '}and{' '}
                      <a href="/privacy-policy#terms-and-conditions" className="text-[#2563eb] hover:text-blue-400 underline transition-colors">Terms &amp; Conditions</a>.
                    </span>
                  </label>
                  {privacyError && (
                    <p className="text-red-400 text-xs mt-1.5 ml-7">Please confirm you have read and agreed to our Privacy Policy and Terms &amp; Conditions.</p>
                  )}
                </div>
                {successMsg && (
                  <div style={{ backgroundColor: '#1e3a5f', border: '1px solid rgba(37,99,235,0.5)', color: '#60a5fa', padding: '10px 14px', fontSize: '14px', textAlign: 'center' }}>
                    ✓ Enquiry sent! We'll be in touch shortly.
                  </div>
                )}
                {errorMsg && (
                  <div style={{ backgroundColor: '#3f1515', border: '1px solid rgba(239,68,68,0.4)', color: '#f87171', padding: '10px 14px', fontSize: '13px', textAlign: 'center' }}>
                    ✗ {errorMsg}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white py-3.5 text-sm font-semibold tracking-wide transition-all duration-300 flex items-center justify-center gap-2 rounded-sm disabled:opacity-50 hover:shadow-lg hover:shadow-[#2563eb]/20 mt-2"
                >
                  {submitting ? 'Sending...' : <>Send Enquiry <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
          </div>
        </div>
      </div>
    </section>
  );
}
